import { createSlice } from '@reduxjs/toolkit';
import { HISTORY_DEFAULT_TIME_RANGE } from './transactionTablesConfig';

const HISTORY_MIN_SEARCH_LENGTH = 3;

const createDefaultHistoryQuery = () => ({
    searchTerm: '',
    timeRange: HISTORY_DEFAULT_TIME_RANGE,
    filters: [],
});

const initialState = {
    tableIdFetchingDataInProgress: '',
    fetchingTableDataError: null,

    tableConfigFetchingInProgress: '',
    fetchingTableConfigError: null,

    tableConfig: {},
    allTablesData: {},

    // History table columns, keyed by location id. Separate from `tableConfig`, whose key is the
    // backend template name, so locations sharing a template (or All Events) cannot collide.
    historyColumnsConfig: {},
    historyColumnsConfigFetching: {},
    historyColumnsConfigError: {},

    historyQuery: {},
};

const tableControlSlice = createSlice({
    name: 'tableControls',
    initialState,
    reducers: {
        fetchTableDataStart(state, { payload: { tableId } }) {
            state.tableIdFetchingDataInProgress = tableId;
            state.fetchingTableDataError = null;
        },
        fetchTableDataFailed(state, { payload: error }) {
            state.tableIdFetchingDataInProgress = '';
            state.fetchingTableDataError = error;
        },
        fetchTableDataSuccess(state, { payload: { data, tableId, pageNum, append } }) {
            state.tableIdFetchingDataInProgress = '';
            const allTablesData = state.allTablesData;

            if (append) {
                allTablesData[tableId][pageNum] = { data };
            } else {
                allTablesData[tableId] = {
                    [pageNum]: { data },
                };
            }
        },

        fetchColumnsConfigStart(state, { payload: { configKey } }) {
            state.tableConfigFetchingInProgress = configKey;
            state.fetchingTableConfigError = null;
        },
        // The key comes from the action, never from `columns.name`: the backend returns the same
        // `name` ('ALL') for every location, so keying off the response body collapsed every
        // location's columns into one entry. See configCacheKey in transactionTablesConfig.
        fetchColumnsConfigSuccess(state, { payload: { configKey, columns } }) {
            state.tableConfig[configKey] = columns;
            state.tableConfigFetchingInProgress = '';
        },
        fetchColumnsConfigFailed(state, { payload: error }) {
            state.tableConfigFetchingInProgress = '';
            state.fetchingTableConfigError = error;
        },

        // Saga trigger only. ensureHistoryColumnsConfig decides whether a fetch is needed.
        fetchHistoryColumnsConfigRequest() {},
        fetchHistoryColumnsConfigStart(state, { payload: { locationId } }) {
            state.historyColumnsConfigFetching[locationId] = true;
            delete state.historyColumnsConfigError[locationId];
        },
        fetchHistoryColumnsConfigSuccess(state, { payload: { locationId, columns } }) {
            // The response's `name` is dropped on purpose - see historyColumnsConfig above.
            state.historyColumnsConfig[locationId] = columns;
            delete state.historyColumnsConfigFetching[locationId];
            delete state.historyColumnsConfigError[locationId];
        },
        fetchHistoryColumnsConfigFailed(state, { payload: { locationId, error } }) {
            delete state.historyColumnsConfigFetching[locationId];
            state.historyColumnsConfigError[locationId] = error;
        },

        setAllTablesData(state, { payload: newAllTablesData }) {
            state.allTablesData = newAllTablesData;
        },

        resetTableControlSlice(state) {
            state.allTablesData = {};
            state.tableConfig = {};
            state.historyColumnsConfig = {};
            state.historyColumnsConfigFetching = {};
            state.historyColumnsConfigError = {};
            state.historyQuery = {};
        },

        removeTable(state, { payload: tableId }) {
            // eslint-disable-next-line no-unused-vars
            const { [tableId]: toBeDeleted, ...rest } = state.allTablesData;
            state.allTablesData = rest;
            if (state.historyQuery[tableId]) {
                // eslint-disable-next-line no-unused-vars
                const { [tableId]: _, ...restQuery } = state.historyQuery;
                state.historyQuery = restQuery;
            }
        },

        // Rows only: column templates do not change on submit, so historyColumnsConfig stays.
        invalidateLocationTables(state, { payload: locationId }) {
            const manualKey = `${locationId}_manualAnnouncements`;

            if (state.allTablesData[locationId]) {
                delete state.allTablesData[locationId];
            }

            if (state.allTablesData[manualKey]) {
                delete state.allTablesData[manualKey];
            }
        },

        setHistorySearchTerm(state, { payload: { tableId, searchTerm } }) {
            const current = state.historyQuery[tableId] || createDefaultHistoryQuery();
            const raw = typeof searchTerm === 'string' ? searchTerm : '';
            const trimmed = raw.trim();
            const safeSearchTerm = trimmed.length > 0 && trimmed.length < HISTORY_MIN_SEARCH_LENGTH ? (current.searchTerm ?? '') : raw;
            state.historyQuery[tableId] = { ...current, searchTerm: safeSearchTerm };
            if (state.allTablesData[tableId]) delete state.allTablesData[tableId];
        },
        setHistoryTimeRange(state, { payload: { tableId, timeRange } }) {
            const current = state.historyQuery[tableId] || createDefaultHistoryQuery();
            state.historyQuery[tableId] = { ...current, timeRange: timeRange || HISTORY_DEFAULT_TIME_RANGE };
            if (state.allTablesData[tableId]) delete state.allTablesData[tableId];
        },
        setHistoryFilters(state, { payload: { tableId, filters } }) {
            const current = state.historyQuery[tableId] || createDefaultHistoryQuery();
            state.historyQuery[tableId] = { ...current, filters: Array.isArray(filters) ? filters : [] };
            if (state.allTablesData[tableId]) delete state.allTablesData[tableId];
        },
        resetHistoryQuery(state, { payload: { tableId } }) {
            state.historyQuery[tableId] = createDefaultHistoryQuery();
            if (state.allTablesData[tableId]) delete state.allTablesData[tableId];
        },
        resetAllHistoryQueries(state) {
            const tableIds = Object.keys(state.historyQuery);
            for (const tableId of tableIds) {
                if (state.allTablesData[tableId]) delete state.allTablesData[tableId];
            }
            state.historyQuery = {};
        },
    },
});

export const tableControlReducer = tableControlSlice.reducer;
export const tableControlActions = tableControlSlice.actions;
